import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Collapse, { type CollapseProps } from '@mui/material/Collapse';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { useState } from 'react';

type BaseCollapsibleProps = {
  titleRender?: React.ReactNode;
  disableMargin?: boolean;
  children: React.ReactNode;
} & CollapseProps;

type ControlledCollapsibleProps = {
  expanded: boolean;
  onExpandToggle: (expanded: boolean) => void;
} & BaseCollapsibleProps;

type UncontrolledCollapsibleProps = {
  isInitialExpanded?: boolean;
  expanded?: undefined;
} & BaseCollapsibleProps;

export type CollapsibleProps = ControlledCollapsibleProps | UncontrolledCollapsibleProps;

export const Collapsible = ({ titleRender, children, disableMargin, ...collapseProps }: CollapsibleProps) => {
  const isControlled = typeof collapseProps.expanded === 'boolean';
  const [internalExpanded, setInternalExpanded] = useState(
    isControlled ? true : (collapseProps.isInitialExpanded ?? true)
  );
  const expanded = isControlled ? collapseProps.expanded : internalExpanded;

  const handleExpandClick = () => {
    if (isControlled) {
      collapseProps.onExpandToggle(!expanded);
    } else {
      setInternalExpanded(expanded => !expanded);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'flex-start',
          marginBottom: disableMargin ? 0 : 2,
        }}
      >
        {titleRender}
        <StyledExpandMore expand={expanded} onClick={handleExpandClick} sx={{ marginLeft: titleRender ? 1.5 : 0 }}>
          <ExpandMoreIcon data-test="collapsible-icon" />
        </StyledExpandMore>
      </Box>

      <Collapse in={expanded}>{children}</Collapse>
    </Box>
  );
};

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const StyledExpandMore = styled((props: ExpandMoreProps) => {
  const { expand: _expand, ...rest } = props;
  return <IconButton {...rest} />;
})(({ theme, expand }) => ({
  transform: expand ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));
